import { PLATFORM_UI_CONFIG } from "../config/app.config";

/**
 * Calculate the dynamic header height
 * Base header height (4rem = 64px) + platform-specific status bar padding
 */
function getHeaderHeight(): number {
  const baseHeaderHeight = 64; // 4rem in pixels (1rem = 16px)
  const statusBarPadding = PLATFORM_UI_CONFIG.getStatusBarPadding();
  return baseHeaderHeight + statusBarPadding;
}

/**
 * Export function to get header height for use in components
 */
export function getAppHeaderHeight(): number {
  return getHeaderHeight();
}

export function enableStickyWatcher(selector = '[data-watch-sticky]') {
  const elements = document.querySelectorAll(selector);

  elements.forEach(el => {
    if (!(el instanceof HTMLElement)) return;
    
    // Apply dynamic top position if data-sticky-top is "auto"
    const dataTopValue = el.getAttribute('data-sticky-top');
    if (dataTopValue === 'auto') {
      el.style.top = `${getHeaderHeight()}px`;
    }

    const stickyProgress = (el: HTMLElement, zone_height: number) => {
      // Calculate sticky position: use data-sticky-top if provided, otherwise use dynamic header height
      const dataTopValue = el.getAttribute('data-sticky-top');
      let sticky_top: number;
      
      if (dataTopValue) {
        // If data-sticky-top is set to "auto" or not a number, use dynamic header height
        if (dataTopValue === 'auto' || isNaN(parseInt(dataTopValue))) {
          sticky_top = getHeaderHeight();
        } else {
          // Legacy support: multiply by 4 to convert from rem-like units to pixels
          // This maintains backward compatibility
          sticky_top = parseInt(dataTopValue) * 4;
        }
      } else {
        // Default to dynamic header height
        sticky_top = getHeaderHeight();
      }

      const top = el.getBoundingClientRect().top;
      const progress = Math.max(0, Math.min(1, 1 - (top - sticky_top) / zone_height));

      if (progress > 0) {
        el.style.backgroundColor = `rgba(229, 215, 222, ${0.9 * progress})`;
        el.style.backdropFilter = `blur(${5 * progress}px)`;
        el.style.fontSize = `${2 - (2 - 1.5) * progress}rem`;

        if (progress >= 1) {
          el.classList.add('stuck');
        }
        else {
          el.classList.remove('stuck');
        }
      }
      else {
        el.style.backgroundColor = 'rgba(229, 215, 222, 0)';
        el.style.backdropFilter = 'blur(0px)';
        el.style.fontSize = '2rem';
        el.classList.remove('stuck');
      }
    };

    window.addEventListener('scroll', () => stickyProgress(el, 24));
    window.addEventListener('resize', () => stickyProgress(el, 24));
    
    // Robust scroll restoration handling:
    // Check multiple times over 500ms to catch scroll position restoration at various timings
    const checkTimes = [0, 16, 50, 100, 200, 350, 500]; // Progressive delays in ms
    let lastScrollY = window.scrollY;
    let checksRemaining = checkTimes.length;
    
    checkTimes.forEach((delay, index) => {
      setTimeout(() => {
        stickyProgress(el, 24);
        
        // Track if scroll position changed since last check
        const currentScrollY = window.scrollY;
        if (currentScrollY !== lastScrollY) {
          lastScrollY = currentScrollY;
          // If scroll changed, do one more check after a brief delay
          if (index === checkTimes.length - 1) {
            setTimeout(() => stickyProgress(el, 24), 100);
          }
        }
        
        checksRemaining--;
      }, delay);
    });
    
    // Also listen for scroll restoration events (e.g., browser back/forward navigation)
    if ('onscrollend' in window) {
      // Modern browsers with scrollend event - do a final check
      window.addEventListener('scrollend', () => {
        stickyProgress(el, 24);
      }, { once: true });
    }
    
    //console.log("Sticky watcher enabled");
  });
}
